import { useState, useEffect, useRef } from 'react'
import { X, Loader2, Check, Plus, Search } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { applyFUnitSpecTemplates, formatProdAccToValueText } from '@/hooks/useEquipmentSpecifications'
import { AppBarTitle } from '@/components/layout/pageHeader'
import LxBxHInput from './LxBxHInput'
import ManufacturerSearchField from './ManufacturerSearchField'

const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  'R1': 'Chain Sling',
  'R3': 'Wire Rope Sling',
  'R4-2': 'Shackle',
  'R4-4': 'Eye Bolt',
  'R4-11': 'Beam Clamp',
  'R4-12': 'Clamp',
  'R4-15': 'Load Cell',
  'R9-6': 'Master Link',
  'R9-21': 'Hook',
  'FAL-1': 'Fall Harness',
  'FAL-4': 'Lanyard',
  'FAL-7': 'Fall Arrest Block',
}

export interface CreatedEquipment {
  id: string
  name: string
  serial_number: string
  part_number: string | null
  asset_number: string
  company_id: string
  owner_id: string | null
  category_id: string | null
  status: string
  last_ndt_date: string | null
  next_ndt_date: string | null
}

interface EquipmentCreationModalProps {
  isOpen: boolean
  onClose: () => void
  companyId?: string
  onSuccess?: (equipment: CreatedEquipment) => void
}

interface Category {
  id: string
  title_no: string | null
  title_en: string
  code: string
  parent_id: string | null
}

interface CompanyLocation {
  id: string
  name: string
  is_default: boolean
}

interface CompanyOption {
  id: string
  name: string
}

interface Standard {
  id: string
  code: string
  title: string
}

interface SpecTemplate {
  id: string
  spec_key: string
  label_no: string | null
  label_en: string
  data_type: string
  default_unit_id: string | null
  unit_symbol?: string
  is_required: boolean
  display_order: number
  default_value?: string
  validation_rules?: any
  reference_table?: string
}

const F1_DEFAULT_STANDARD_CODES = ['DNV 2.7-1', 'ISO 10855-1']

const isF1Category = (code?: string | null) =>
  !!code && (code === 'F1' || code.startsWith('F1-'))

// Shared field class for all inputs / selects
const fieldCls =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#1E3A5F] bg-white text-slate-800 transition-colors'
const fieldStyle = { minHeight: 44 }

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
    {children}
  </p>
)

const EquipmentCreationModal = ({ isOpen, onClose, companyId, onSuccess }: EquipmentCreationModalProps) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [companyName, setCompanyName] = useState<string>('')
  const [companyLocations, setCompanyLocations] = useState<CompanyLocation[]>([])
  const [companies, setCompanies] = useState<CompanyOption[]>([])
  const [companyQuery, setCompanyQuery] = useState('')
  const [companyOpen, setCompanyOpen] = useState(false)
  const companyBlurRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [dataLoading, setDataLoading] = useState(true)

  const [parentCategories, setParentCategories] = useState<Category[]>([])
  const [selectedParentId, setSelectedParentId] = useState<string>('')
  const [subCategories, setSubCategories] = useState<Category[]>([])

  const [formData, setFormData] = useState({
    name: '',
    serial_number: '',
    part_number: '',
    location: '',
    company_id: companyId,
    category_id: '',
    status: 'Aktiv',
    owner_id: '',
  })

  const [specTemplates, setSpecTemplates] = useState<SpecTemplate[]>([])
  const [specValues, setSpecValues] = useState<Record<string, any>>({})
  const [specsLoading, setSpecsLoading] = useState(false)

  const [categoryStandards, setCategoryStandards] = useState<Standard[]>([])

  // prod_acc_to multi-select state
  const [showAddStandard, setShowAddStandard] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '', serial_number: '', part_number: '',
        location: '', company_id: companyId || '',
        category_id: '', status: 'Aktiv', owner_id: '',
      })
      setSelectedParentId('')
      setSubCategories([])
      setSpecTemplates([])
      setSpecValues({})
      setCategoryStandards([])
      setShowAddStandard(false)
      setCompanyQuery('')
      setCompanyOpen(false)
      setCompanyName('')
      setCompanyLocations([])
      setError(null)
      loadData()
    }
  }, [isOpen, companyId])

  useEffect(() => {
    if (selectedParentId) {
      const parent = categories.find(c => c.id === selectedParentId)
      if (parent) {
        const subs = categories.filter(c => c.parent_id === selectedParentId)
        setSubCategories(subs.length > 0 ? subs : [parent])
      }
      setFormData(prev => ({ ...prev, category_id: '', name: '' }))
    } else {
      setSubCategories([])
    }
  }, [selectedParentId, categories])

  useEffect(() => {
    if (formData.category_id) {
      const selectedCat = categories.find(c => c.id === formData.category_id)
      if (selectedCat) {
        const displayName =
          CATEGORY_DISPLAY_NAMES[selectedCat.code] || selectedCat.title_no || selectedCat.title_en
        setFormData(prev => ({ ...prev, name: displayName }))
      }
    }
  }, [formData.category_id, categories])

  const loadLocations = async (id: string) => {
    const { data } = await supabase
      .from('company_locations')
      .select('id, name, is_default')
      .eq('company_id', id)
      .order('name')
    setCompanyLocations(data || [])
    const defaultLoc = data?.find(loc => loc.is_default)
    setFormData(prev => ({ ...prev, location: defaultLoc?.name || '' }))
  }

  const applyCompany = (company: CompanyOption | null) => {
    if (!company) {
      setFormData(prev => ({ ...prev, company_id: '', location: '' }))
      setCompanyName('')
      setCompanyLocations([])
      return
    }
    setFormData(prev => ({ ...prev, company_id: company.id, location: '' }))
    setCompanyName(company.name)
    setCompanyQuery(company.name)
    setCompanyOpen(false)
    loadLocations(company.id)
  }

  const loadData = async () => {
    try {
      setDataLoading(true)
      const [categoriesResult, companiesResult] = await Promise.all([
        supabase.from('categories_v2').select('id, title_no, title_en, code, parent_id').order('code'),
        supabase.from('companies').select('id, name').order('name', { ascending: true }),
      ])

      if (categoriesResult.data) {
        setCategories(categoriesResult.data)
        setParentCategories(categoriesResult.data.filter(c => !c.parent_id))
      }
      const companyList = companiesResult.data || []
      setCompanies(companyList)

      if (companyId) {
        const match = companyList.find(c => c.id === companyId)
        if (match) {
          setCompanyName(match.name)
          setCompanyQuery(match.name)
          setFormData(prev => ({ ...prev, company_id: match.id }))
        }
        await loadLocations(companyId)
      }
    } catch {
      setError('Failed to load form data')
    } finally {
      setDataLoading(false)
    }
  }

  const loadCategoryReferenceData = async (categoryId: string): Promise<Standard[]> => {
    try {
      const category = categories.find(c => c.id === categoryId)
      const categoryIds = [categoryId, ...(category?.parent_id ? [category.parent_id] : [])]

      const { data: stdCatData } = await supabase
        .from('standards_categories')
        .select('standard:standards_v2(id, code, title)')
        .in('category_id', categoryIds)

      let standards: Standard[] = []
      if (stdCatData && stdCatData.length > 0) {
        const stds = stdCatData.map((s: any) => s.standard).filter(Boolean)
          .sort((a: Standard, b: Standard) => a.code.localeCompare(b.code))
        standards = Array.from(new Map(stds.map((s: Standard) => [s.id, s])).values()) as Standard[]
      } else {
        const { data: allStds } = await supabase
          .from('standards_v2').select('id, code, title').order('code')
        standards = allStds || []
      }
      setCategoryStandards(standards)
      return standards
    } catch {
      setCategoryStandards([])
      return []
    }
  }

  const loadSpecTemplates = async () => {
    if (!formData.category_id) return
    setSpecsLoading(true)
    try {
      let { data: templates, error: templatesError } = await supabase
        .from('specification_templates_with_categories')
        .select('*')
        .eq('category_id', formData.category_id)
        .eq('is_visible', true)
        .order('display_order', { ascending: true })

      if (templatesError || !templates || templates.length === 0) {
        const { data: mappings } = await supabase
          .from('category_specification_templates_v2')
          .select('*, template:canonical_specification_templates(*)')
          .eq('category_id', formData.category_id)
          .eq('is_visible', true)
          .order('display_order', { ascending: true })

        if (mappings && mappings.length > 0) {
          templates = mappings.map((m: any) => ({
            ...m.template,
            category_id: m.category_id,
            is_required: m.is_required,
            display_order: m.display_order,
          }))
        }
      }

      if (templates && templates.length > 0) {
        const unitIds = templates.map(t => t.default_unit_id).filter(Boolean)
        if (unitIds.length > 0) {
          const { data: units } = await supabase
            .from('units_v2').select('id, symbol').in('id', unitIds)
          if (units) {
            const unitMap = Object.fromEntries(units.map(u => [u.id, u.symbol]))
            templates = templates.map(t => ({
              ...t,
              unit_symbol: t.default_unit_id ? unitMap[t.default_unit_id] : undefined,
            }))
          }
        }
      }

      const categoryCode = categories.find(c => c.id === formData.category_id)?.code
      templates = await applyFUnitSpecTemplates(templates || [], categoryCode)

      let standards = await loadCategoryReferenceData(formData.category_id)
      if (isF1Category(categoryCode)) {
        const missingCodes = F1_DEFAULT_STANDARD_CODES.filter(
          code => !standards.some(s => s.code === code),
        )
        if (missingCodes.length > 0) {
          const { data: extra } = await supabase
            .from('standards_v2')
            .select('id, code, title')
            .in('code', missingCodes)
          if (extra && extra.length > 0) {
            standards = [...standards, ...extra]
            setCategoryStandards(standards)
          }
        }
      }

      setSpecTemplates(templates)
      const initialValues: Record<string, any> = {}
      for (const t of (templates || [])) {
        if (t.spec_key === 'prod_acc_to') {
          initialValues[t.spec_key] = isF1Category(categoryCode)
            ? [...F1_DEFAULT_STANDARD_CODES]
            : []
        } else {
          initialValues[t.spec_key] = t.default_value || ''
        }
      }
      setSpecValues(initialValues)
    } catch {
      /* silent */
    } finally {
      setSpecsLoading(false)
    }
  }

  useEffect(() => {
    if (!formData.category_id) {
      setSpecTemplates([])
      setSpecValues({})
      setCategoryStandards([])
      setShowAddStandard(false)
      setSpecsLoading(false)
      return
    }
    loadSpecTemplates()
  }, [formData.category_id])

  const handleSubmit = async () => {
    setError(null)
    if (!formData.company_id) { setError('Velg selskap'); return }
    if (!formData.category_id) { setError('Velg utstyrstype'); return }
    if (!formData.serial_number.trim()) { setError('Serienummer er påkrevd'); return }
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setError('Bruker ikke autentisert'); return }

      const { data: assetNumber, error: assetError } = await supabase.rpc(
        'generate_company_asset_number',
        { company_id_param: formData.company_id },
      )
      if (assetError) throw new Error('Kunne ikke generere ID-nummer')

      const manufacturerId = specValues['manufacturer'] || null

      const { data: equipment, error: equipmentError } = await supabase
        .from('equipment_v2')
        .insert({
          asset_number: assetNumber,
          serial_number: formData.serial_number,
          name: formData.name,
          part_number: formData.part_number || null,
          company_id: formData.company_id,
          category_id: formData.category_id,
          manufacturer_id: manufacturerId,
          status: formData.status,
          location: formData.location || null,
          owner_id: formData.owner_id || null,
          created_by: user.id,
        })
        .select('id, name, serial_number, part_number, asset_number, company_id, owner_id, category_id, status, last_ndt_date, next_ndt_date')
        .single()

      if (equipmentError || !equipment) throw new Error('Kunne ikke opprette utstyr')

      if (specTemplates.length > 0) {
        const specs = specTemplates.map(t => {
          const value = specValues[t.spec_key]
          const isEmpty =
            value === '' || value === null || value === undefined ||
            (Array.isArray(value) && value.length === 0)

          if (isEmpty) return null

          const spec: any = {
            equipment_id: equipment.id,
            template_id: t.id,
            spec_key: t.spec_key,
            unit_id: t.default_unit_id,
            created_by: user.id,
            updated_by: user.id,
            validation_status: 'pending',
          }

          if (t.spec_key === 'manufacturer' || t.spec_key === 'manufacturer_id') {
            spec.value_reference_id = value
          } else if (t.spec_key === 'prod_acc_to') {
            const text = formatProdAccToValueText(
              value,
              id => categoryStandards.find(s => s.id === id)?.code,
            )
            if (!text) return null
            spec.value_text = text
          } else {
            switch (t.data_type) {
              case 'numeric':
              case 'decimal':
              case 'angle':
              case 'currency':
              case 'percentage':
                spec.value_numeric = parseFloat(String(value).replace(',', '.'))
                break
              case 'integer':
              case 'year':
                spec.value_integer = parseInt(value, 10)
                break
              case 'boolean':
                spec.value_boolean = value === 'true' || value === true
                break
              case 'date':
                spec.value_date = value
                break
              case 'reference':
                spec.value_reference_id = value
                break
              default:
                spec.value_text = String(value)
            }
          }
          return spec
        }).filter(Boolean)

        if (specs.length > 0) {
          const { error: specsError } = await supabase
            .from('equipment_specifications_v2')
            .insert(specs)
          if (specsError) console.error('Specs error:', specsError)
        }
      }

      onSuccess?.({
        id: equipment.id,
        name: equipment.name || formData.name,
        serial_number: equipment.serial_number || formData.serial_number,
        part_number: equipment.part_number ?? null,
        asset_number: equipment.asset_number || assetNumber,
        company_id: equipment.company_id || formData.company_id,
        owner_id: equipment.owner_id ?? null,
        category_id: equipment.category_id ?? formData.category_id,
        status: equipment.status || formData.status,
        last_ndt_date: equipment.last_ndt_date ?? null,
        next_ndt_date: equipment.next_ndt_date ?? null,
      })
      onClose()
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '', serial_number: '', part_number: '',
      location: '', company_id: companyId || '',
      category_id: '', status: 'Aktiv', owner_id: '',
    })
    setSelectedParentId('')
    setSubCategories([])
    setSpecTemplates([])
    setSpecValues({})
    setCategoryStandards([])
    setShowAddStandard(false)
    setCompanyQuery('')
    setCompanyOpen(false)
    if (!companyId) {
      setCompanyName('')
      setCompanyLocations([])
    }
    setError(null)
  }

  const getCategoryDisplayName = (cat: Category) =>
    CATEGORY_DISPLAY_NAMES[cat.code] || cat.title_en || cat.title_no

  const getSpecLabel = (t: SpecTemplate) => t.label_no || t.label_en || t.spec_key

  const renderSpecField = (t: SpecTemplate, className = '') => {
    const isManufacturer = t.spec_key === 'manufacturer' || t.spec_key === 'manufacturer_id' || t.spec_key === 'produsent'
    const isStandards = t.spec_key === 'prod_acc_to' || t.spec_key === 'production_standard_id'
    return (
      <div key={t.id} className={className}>
        <Label>
          {getSpecLabel(t)}
          {t.is_required ? ' *' : ''}
          {t.unit_symbol && !isManufacturer && !isStandards && (
            <span className="normal-case tracking-normal font-normal text-slate-300 ml-1">
              ({t.unit_symbol})
            </span>
          )}
        </Label>
        {renderSpecInput(t)}
      </div>
    )
  }

  const findSpec = (...keys: string[]) => specTemplates.find(t => keys.includes(t.spec_key))

  const renderSpecsSection = () => {
    if (!formData.category_id) return null
    if (specsLoading) {
      return (
        <div className="rounded-lg bg-slate-100 border border-slate-200 px-4 py-8 flex items-center justify-center gap-3">
          <Loader2 size={20} className="animate-spin text-[#1E3A5F]" />
          <span className="text-sm text-slate-400">Laster egenskaper...</span>
        </div>
      )
    }
    if (specTemplates.length === 0) return null

    const payload = findSpec('payload')
    const tare = findSpec('tare')
    const mgw = findSpec('mgw')
    const dimensions = findSpec('dimensions')
    const ndt = findSpec('ndt_date', 'last_ndt_date')
    const certificate = findSpec('certificate', 'cert_no', 'certificate_no', 'sertifikat')
      || specTemplates.find(t => /sertifikat|certificate/i.test(t.label_no || t.label_en || t.spec_key))
    const fire = findSpec('fire')
    const manufacturer = findSpec('manufacturer', 'manufacturer_id', 'produsent')
    const prodYear = findSpec('prod_year', 'manufacture_year', 'prod_år')
      || specTemplates.find(t => /prod\.?\s*år/i.test(t.label_no || t.label_en || ''))
    const featuredKeys = new Set(
      [payload, tare, mgw, dimensions, ndt, certificate, fire, manufacturer, prodYear]
        .filter(Boolean)
        .map(t => t!.spec_key),
    )
    const rest = specTemplates.filter(
      t => t.spec_key !== 'mgw_at' && !featuredKeys.has(t.spec_key),
    )
    const weightRow = [payload, tare, mgw].filter(Boolean) as SpecTemplate[]

    return (
      <div className="space-y-4 rounded-lg bg-slate-100 border border-slate-200 px-4 py-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
          Egenskaper
        </p>
        {weightRow.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {weightRow.map(t => renderSpecField(t))}
          </div>
        )}
        {dimensions && renderSpecField(dimensions)}
        {(ndt || certificate) && (
          <div className="grid grid-cols-2 gap-3">
            {ndt && renderSpecField(ndt)}
            {certificate && renderSpecField(certificate)}
          </div>
        )}
        {(manufacturer || prodYear) && (
          <div className="grid grid-cols-2 gap-3">
            {manufacturer && renderSpecField(manufacturer)}
            {prodYear && renderSpecField(prodYear)}
          </div>
        )}
        {fire && renderSpecField(fire)}
        {rest.length > 0 && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-4">
            {rest.map(t => {
              const isStandards =
                t.spec_key === 'prod_acc_to' || t.spec_key === 'production_standard_id'
              return renderSpecField(t, isStandards ? 'col-span-2' : '')
            })}
          </div>
        )}
      </div>
    )
  }

  const setSpec = (key: string, value: any) =>
    setSpecValues(prev => ({ ...prev, [key]: value }))

  const renderSpecInput = (t: SpecTemplate) => {
    const value = specValues[t.spec_key]

    // Manufacturer — searchable select of all manufacturers
    if (
      t.spec_key === 'manufacturer' ||
      t.spec_key === 'manufacturer_id' ||
      t.spec_key === 'produsent' ||
      t.reference_table === 'manufacturers_v2'
    ) {
      return (
        <ManufacturerSearchField
          value={value || ''}
          onChange={id => setSpec(t.spec_key, id)}
        />
      )
    }

    // Standards — tag-based multi-select; persist/display codes, not UUIDs
    if (
      t.spec_key === 'prod_acc_to' ||
      t.spec_key === 'production_standard_id' ||
      t.reference_table === 'standards_v2'
    ) {
      const currentCodes: string[] = Array.isArray(value) ? value : value ? [value] : []
      const available = categoryStandards.filter(
        s => !currentCodes.includes(s.code) && !currentCodes.includes(s.id),
      )
      return (
        <div className="border border-slate-300 rounded-md px-3 py-2 bg-white" style={fieldStyle}>
          <div className="flex flex-wrap gap-1">
            {currentCodes.map(code => {
              const std = categoryStandards.find(s => s.code === code || s.id === code)
              const label = std?.code || (!code.match(/^[0-9a-f-]{36}$/i) ? code : null)
              return label ? (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded"
                >
                  {label}
                  <button
                    type="button"
                    onClick={() => {
                      setSpec(t.spec_key, currentCodes.filter(c => c !== code))
                      setShowAddStandard(false)
                    }}
                    className="text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ) : null
            })}
            {available.length > 0 && !showAddStandard && (
              <button
                type="button"
                onClick={() => setShowAddStandard(true)}
                className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs font-medium text-[#1E3A5F] border border-dashed border-[#1E3A5F] rounded hover:bg-slate-50 transition-colors"
              >
                <Plus size={10} />
                Legg til
              </button>
            )}
            {showAddStandard && (
              <select
                autoFocus
                defaultValue=""
                onChange={e => {
                  if (e.target.value) {
                    setSpec(t.spec_key, [...currentCodes, e.target.value])
                    setShowAddStandard(false)
                  }
                }}
                onBlur={() => setShowAddStandard(false)}
                className="text-xs text-slate-800 border-b border-[#1E3A5F] bg-transparent focus:outline-none py-0.5"
              >
                <option value="">Velg standard...</option>
                {available.map(s => (
                  <option key={s.id} value={s.code}>{s.code}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )
    }

    // LxBxH — three numeric fields with "x" separators
    if (t.spec_key === 'dimensions') {
      return (
        <LxBxHInput
          variant="field"
          value={value || ''}
          onChange={next => setSpec(t.spec_key, next)}
        />
      )
    }

    // Fire extinguisher — checkbox stored as "Ja"
    if (t.spec_key === 'fire') {
      const checked = value === 'Ja' || value === true || value === 'true'
      return (
        <label
          className={`flex items-center gap-2.5 px-3 border rounded-md cursor-pointer transition-colors ${
            checked ? 'border-[#1E3A5F] bg-[#1E3A5F]/5' : 'border-slate-300 hover:bg-slate-50'
          }`}
          style={fieldStyle}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setSpec(t.spec_key, e.target.checked ? 'Ja' : '')}
            className="w-5 h-5 rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F] shrink-0"
          />
          <span className="text-sm font-semibold text-slate-800">Ja</span>
        </label>
      )
    }

    // Boolean
    if (t.data_type === 'boolean') {
      return (
        <select
          value={value || ''}
          onChange={e => setSpec(t.spec_key, e.target.value)}
          className={fieldCls}
          style={fieldStyle}
        >
          <option value="">Velg...</option>
          <option value="true">Ja</option>
          <option value="false">Nei</option>
        </select>
      )
    }

    // Date
    if (t.data_type === 'date') {
      return (
        <input
          type="date"
          value={value || ''}
          onChange={e => setSpec(t.spec_key, e.target.value)}
          className={fieldCls}
          style={fieldStyle}
        />
      )
    }

    // Enum
    if (t.data_type === 'enum' && t.validation_rules?.enum_options) {
      return (
        <select
          value={value || ''}
          onChange={e => setSpec(t.spec_key, e.target.value)}
          className={fieldCls}
          style={fieldStyle}
        >
          <option value="">Velg...</option>
          {t.validation_rules.enum_options.map((opt: string) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )
    }

    // All numeric types → text + inputMode (no spinners)
    const inputMode =
      t.data_type === 'integer' || t.data_type === 'year'
        ? 'numeric'
        : ['numeric', 'decimal', 'angle', 'currency', 'percentage'].includes(t.data_type)
        ? 'decimal'
        : 'text'

    return (
      <input
        type="text"
        inputMode={inputMode as any}
        value={value || ''}
        onChange={e => setSpec(t.spec_key, e.target.value)}
        placeholder={
          t.data_type === 'year' ? 'YYYY'
          : t.unit_symbol ? `0 ${t.unit_symbol}`
          : ''
        }
        className={fieldCls}
        style={fieldStyle}
      />
    )
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed right-0 bottom-0 z-40 flex flex-col bg-white"
      style={{ top: 'var(--topnav-height)', left: 'var(--rail-width)' }}
    >
      <AppBarTitle
        title="Nytt utstyr"
        subtitle={companyName}
        onBack={onClose}
      />
      <div className="bg-white w-full h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto px-6 pb-5">
          <div className="max-w-3xl mx-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 mb-4 mt-4">
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          {dataLoading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <Loader2 size={20} className="animate-spin text-[#1E3A5F]" />
              <span className="text-sm text-slate-400">Laster...</span>
            </div>
          ) : (
            <div className="space-y-4 pt-4">
              <div>
                <Label>Selskap *</Label>
                {(() => {
                  const selected = companies.find(c => c.id === formData.company_id)
                  const filterText = selected && companyQuery === selected.name ? '' : companyQuery
                  const filtered = companies.filter(c =>
                    c.name.toLowerCase().includes(filterText.trim().toLowerCase()),
                  )
                  return (
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={companyOpen ? companyQuery : (selected?.name || companyQuery)}
                        placeholder="Søk selskap..."
                        autoFocus={!companyId}
                        onFocus={() => {
                          if (companyBlurRef.current) clearTimeout(companyBlurRef.current)
                          setCompanyQuery(selected?.name || companyQuery)
                          setCompanyOpen(true)
                        }}
                        onBlur={() => {
                          companyBlurRef.current = setTimeout(() => setCompanyOpen(false), 150)
                        }}
                        onChange={e => {
                          setCompanyQuery(e.target.value)
                          setCompanyOpen(true)
                          if (selected && e.target.value !== selected.name) applyCompany(null)
                        }}
                        className={`${fieldCls} pl-9`}
                        style={fieldStyle}
                      />
                      {companyOpen && (
                        <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg">
                          {filtered.length === 0 ? (
                            <p className="px-3 py-2 text-sm text-slate-400">Ingen treff</p>
                          ) : (
                            filtered.map(c => (
                              <button
                                key={c.id}
                                type="button"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => applyCompany(c)}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                                  c.id === formData.company_id ? 'bg-[#1E3A5F]/5 text-[#1E3A5F] font-semibold' : 'text-slate-800'
                                }`}
                              >
                                {c.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kategori *</Label>
                  <select
                    value={selectedParentId}
                    onChange={e => setSelectedParentId(e.target.value)}
                    className={fieldCls}
                    style={fieldStyle}
                  >
                    <option value="">Velg...</option>
                    {parentCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.code} – {cat.title_en || cat.title_no}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Utstyrstype *</Label>
                  <select
                    value={formData.category_id}
                    onChange={e => setFormData({ ...formData, category_id: e.target.value })}
                    disabled={!selectedParentId}
                    className={`${fieldCls} disabled:opacity-40 disabled:bg-slate-50 disabled:cursor-not-allowed`}
                    style={fieldStyle}
                  >
                    <option value="">Velg...</option>
                    {subCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {getCategoryDisplayName(cat)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Serienummer *</Label>
                  <input
                    type="text"
                    value={formData.serial_number}
                    onChange={e => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="SN-12345"
                    className={fieldCls}
                    style={fieldStyle}
                  />
                </div>
                <div>
                  <Label>Delenummer</Label>
                  <input
                    type="text"
                    value={formData.part_number}
                    onChange={e => setFormData({ ...formData, part_number: e.target.value })}
                    placeholder="PN-12345"
                    className={fieldCls}
                    style={fieldStyle}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Eiers ID</Label>
                  <input
                    type="text"
                    value={formData.owner_id}
                    onChange={e => setFormData({ ...formData, owner_id: e.target.value })}
                    placeholder="Eiers ID"
                    className={fieldCls}
                    style={fieldStyle}
                  />
                </div>
                <div>
                  <Label>Lokasjon</Label>
                  {!formData.company_id ? (
                    <input
                      type="text"
                      disabled
                      placeholder="Velg selskap først"
                      className={`${fieldCls} disabled:opacity-40 disabled:bg-slate-50 disabled:cursor-not-allowed`}
                      style={fieldStyle}
                    />
                  ) : companyLocations.length > 0 ? (
                    <select
                      value={formData.location}
                      onChange={e => setFormData({ ...formData, location: e.target.value })}
                      className={fieldCls}
                      style={fieldStyle}
                    >
                      <option value="">Velg...</option>
                      {companyLocations.map(loc => (
                        <option key={loc.id} value={loc.name}>{loc.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.location}
                      onChange={e => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Lokasjon"
                      className={fieldCls}
                      style={fieldStyle}
                    />
                  )}
                </div>
              </div>

              {renderSpecsSection()}
            </div>
          )}
          </div>
        </div>

        <div className="border-t border-slate-200 px-6 py-3 bg-white shrink-0">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center h-11 px-4 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
            >
              Avbryt
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || specsLoading}
              className="flex-1 flex items-center justify-center gap-2 h-11 px-4 text-sm font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /><span>Oppretter...</span></>
              ) : (
                <><Check size={16} /><span>Opprett utstyr</span></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EquipmentCreationModal
